import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useStripe } from '../contexts/StripeContext';
import { useTheme } from '../contexts/ThemeContext';

export default function Navigation() {
  const { currentUser, signInWithGoogle, logout } = useAuth();
  const { credits, checkCredits } = useStripe();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    if (currentUser) checkCredits();
  }, [currentUser, checkCredits]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node))
        setMobileMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // close mobile menu on route change
  useEffect(() => setMobileMenuOpen(false), [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  const navLinkClass = (path: string) =>
    `nav-link text-sm ${isActive(path) ? 'active' : ''}`;

  return (
    <nav
      className={`nav-bar transition-shadow duration-300 ${scrolled ? 'shadow-lg' : ''}`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <img
              src="/DTM-logo-1.png"
              alt="Deep Thought Matrix"
              className="h-9 w-auto transition-transform duration-200 group-hover:scale-105"
            />
            <span className="text-base font-bold gradient-text hidden sm:block">
              Document Summarizer
            </span>
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-6">
            {currentUser ? (
              <>
                <Link to="/home" className={navLinkClass('/home')}>
                  Summarize
                </Link>
                <Link to="/documents" className={navLinkClass('/documents')}>
                  History
                </Link>
                <Link to="/credits" className={navLinkClass('/credits')}>
                  Credits
                </Link>
                <Link to="/pricing" className={navLinkClass('/pricing')}>
                  Pricing
                </Link>
              </>
            ) : (
              <>
                <Link to="/" className={navLinkClass('/')}>
                  Home
                </Link>
                <Link to="/pricing" className={navLinkClass('/pricing')}>
                  Pricing
                </Link>
              </>
            )}
          </div>

          {/* Desktop right controls */}
          <div className="hidden md:flex items-center gap-3">
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg theme-button-outline text-base leading-none"
              title="Toggle theme"
            >
              {theme === 'light' ? '🌙' : '☀️'}
            </button>

            {currentUser ? (
              <>
                {/* Credits pill */}
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
                  style={{ background: 'var(--accent-light)', color: 'var(--accent-primary)' }}
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 2a8 8 0 100 16A8 8 0 0010 2zm0 2a6 6 0 110 12A6 6 0 0110 4zm0 9a1 1 0 100-2 1 1 0 000 2zm1-5.5V9a1 1 0 01-2 0V9.5a.5.5 0 01-.5-.5V8a1.5 1.5 0 013 0v1a.5.5 0 01-.5.5z" />
                  </svg>
                  <span>{credits} credit{credits !== 1 ? 's' : ''}</span>
                </div>

                {/* User email */}
                <span className="text-xs theme-text-secondary hidden lg:block truncate max-w-[140px]">
                  {currentUser.email}
                </span>

                <button
                  onClick={logout}
                  className="theme-button-outline rounded-lg px-3 py-1.5 text-sm font-medium"
                >
                  Sign out
                </button>
              </>
            ) : (
              <button
                onClick={signInWithGoogle}
                className="flex items-center gap-2 theme-button rounded-lg px-4 py-2 text-sm"
              >
                <GoogleIcon />
                Sign in
              </button>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-lg theme-button-outline"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            <svg className="h-5 w-5 theme-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div
          ref={menuRef}
          className="md:hidden border-t theme-border theme-surface px-4 py-4 space-y-3 shadow-xl"
          style={{ animation: 'slideDown 0.18s ease-out' }}
        >
          {currentUser ? (
            <>
              <div className="flex items-center justify-between pb-3 border-b theme-border">
                <span className="text-sm theme-text-secondary truncate">{currentUser.email}</span>
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold"
                  style={{ background: 'var(--accent-light)', color: 'var(--accent-primary)' }}
                >
                  {credits} credits
                </div>
              </div>
              <Link to="/home" className="block py-2 text-sm font-medium theme-text-primary hover:text-indigo-500 transition-colors">
                Summarize
              </Link>
              <Link to="/documents" className="block py-2 text-sm font-medium theme-text-primary hover:text-indigo-500 transition-colors">
                History
              </Link>
              <Link to="/credits" className="block py-2 text-sm font-medium theme-text-primary hover:text-indigo-500 transition-colors">
                Credits
              </Link>
              <Link to="/pricing" className="block py-2 text-sm font-medium theme-text-primary hover:text-indigo-500 transition-colors">
                Pricing
              </Link>
              <div className="flex items-center justify-between pt-3 border-t theme-border">
                <button onClick={toggleTheme} className="text-sm theme-text-secondary">
                  {theme === 'light' ? '🌙 Dark mode' : '☀️ Light mode'}
                </button>
                <button
                  onClick={logout}
                  className="rounded-lg bg-indigo-600 text-white px-3 py-1.5 text-sm font-semibold hover:bg-indigo-700 transition-colors"
                >
                  Sign out
                </button>
              </div>
            </>
          ) : (
            <>
              <Link to="/" className="block py-2 text-sm font-medium theme-text-primary">Home</Link>
              <Link to="/pricing" className="block py-2 text-sm font-medium theme-text-primary">Pricing</Link>
              <button
                onClick={signInWithGoogle}
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-indigo-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-indigo-700 transition-colors mt-2"
              >
                <GoogleIcon />
                Sign in with Google
              </button>
              <button onClick={toggleTheme} className="text-sm theme-text-secondary pt-1">
                {theme === 'light' ? '🌙 Dark mode' : '☀️ Light mode'}
              </button>
            </>
          )}
        </div>
      )}

      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </nav>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}
